import { useState, useRef } from "react";

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
  bg: "#f8fbff", bgSoft: "#eef6ff", surface: "#ffffff", surface2: "#f7fbff",
  primary: "#6bbcff", primaryStrong: "#2f8fff", primaryDeep: "#155eef", primarySoft: "#e9f4ff",
  text: "#0f172a", textSoft: "#334155", textMuted: "#64748b", textFaint: "#94a3b8",
  border: "#dbe7f3", borderStrong: "#bfd7ea",
  success: "#10b981", successSoft: "#ecfdf5",
  warning: "#f59e0b", warningSoft: "#fffbeb",
  danger: "#ef4444", dangerSoft: "#fef2f2",
  purple: "#8b5cf6", purpleSoft: "#f5f3ff",
};

const shadow = "0 1px 4px rgba(21,94,239,0.06), 0 4px 16px rgba(21,94,239,0.04)";
const shadowHover = "0 4px 20px rgba(21,94,239,0.12)";

// ─── Mock data ────────────────────────────────────────────────────────────────
const USERS = {
  admin: { password: "admin123", role: "admin", name: "Admin" },
  client: { password: "client123", role: "client", name: "Goldstone Capital" },
};

const LEADS = [
  { id: 1, name: "James Thornton", phone: "+44 7911 123456", status: "BOOKED", attempts: 1, lastCalled: "Today 09:14", duration: "3m 12s", transcript: [{ role: "agent", text: "Hi, am I speaking with James?" }, { role: "lead", text: "Yes, speaking." }, { role: "agent", text: "Hi James, I'm calling from Goldstone Capital. We help clients diversify into physical gold investments. Do you have two minutes?" }, { role: "lead", text: "Sure, I've actually been thinking about that recently." }, { role: "agent", text: "That's great to hear. Can I ask — have you invested in gold or precious metals before?" }, { role: "lead", text: "Not directly, no. I have some ETFs but nothing physical." }, { role: "agent", text: "Understood. A lot of our clients come from a similar background. We typically work with people looking to protect a portion of their portfolio. Would you be open to a quick call with one of our senior advisors this week?" }, { role: "lead", text: "Yeah, Thursday afternoon would work." }, { role: "agent", text: "Perfect, I'll get that booked in for you. You'll receive a confirmation shortly. Thanks James!" }] },
  { id: 2, name: "Sarah Mitchell", phone: "+44 7700 987654", status: "CALLBACK", attempts: 2, lastCalled: "Today 09:31", duration: "1m 48s", transcript: [{ role: "agent", text: "Hi, is that Sarah?" }, { role: "lead", text: "Yes, who's this?" }, { role: "agent", text: "Hi Sarah, calling from Goldstone Capital about alternative investment opportunities." }, { role: "lead", text: "I'm actually in a meeting right now, can you call back?" }, { role: "agent", text: "Of course, no problem at all. Would tomorrow morning work?" }, { role: "lead", text: "Yes, after 10 would be fine." }, { role: "agent", text: "Noted. I'll make sure someone calls you after 10 tomorrow. Thank you Sarah." }] },
  { id: 3, name: "David Chen", phone: "+44 7911 456789", status: "NOT_INTERESTED", attempts: 1, lastCalled: "Today 09:44", duration: "0m 52s", transcript: [{ role: "agent", text: "Hi, am I speaking with David?" }, { role: "lead", text: "Yes." }, { role: "agent", text: "Hi David, I'm calling from Goldstone Capital about gold investment opportunities." }, { role: "lead", text: "Not interested thanks." }, { role: "agent", text: "No problem at all, I'll make a note of that. Have a great day David." }] },
  { id: 4, name: "Emma Williams", phone: "+44 7700 111222", status: "BOOKED", attempts: 1, lastCalled: "Today 08:52", duration: "4m 05s", transcript: [{ role: "agent", text: "Hi, is this Emma Williams?" }, { role: "lead", text: "It is, yes." }, { role: "agent", text: "Morning Emma, I'm reaching out from Goldstone Capital. We specialise in physical gold and precious metals for portfolio protection. Is this a good moment?" }, { role: "lead", text: "It's fine yes, what exactly do you offer?" }, { role: "agent", text: "Great question. We offer direct ownership of allocated physical gold, stored in insured vaults. Many clients use it to hedge against inflation and currency risk. What's your current investment focus, if you don't mind me asking?" }, { role: "lead", text: "Mostly equities and property. I've been a bit worried lately." }, { role: "agent", text: "That's a really common concern right now. Our advisors work specifically with clients in your position. Would you be happy to speak with one of them this week?" }, { role: "lead", text: "Yes, Friday morning would suit me." }, { role: "agent", text: "Excellent, I'll book that in for Friday. You'll get a confirmation by email. Thanks so much Emma." }] },
  { id: 5, name: "Robert Hayes", phone: "+44 7911 333444", status: "NO_ANSWER", attempts: 3, lastCalled: "Today 10:01", duration: "—", transcript: [] },
  { id: 6, name: "Lisa Foster", phone: "+44 7700 555666", status: "BOOKED", attempts: 1, lastCalled: "Today 08:31", duration: "2m 38s", transcript: [{ role: "agent", text: "Hi, is that Lisa?" }, { role: "lead", text: "Yes, speaking." }, { role: "agent", text: "Hi Lisa, I'm calling from Goldstone Capital. We help clients protect their wealth through physical gold investments." }, { role: "lead", text: "I've heard of this, I was actually looking into it last month." }, { role: "agent", text: "Perfect timing then. Have you spoken to any advisors yet?" }, { role: "lead", text: "No, not yet." }, { role: "agent", text: "We'd love to be your first stop. Our senior advisors are available this week — would Wednesday work for a 20-minute call?" }, { role: "lead", text: "Wednesday at 2pm is fine." }, { role: "agent", text: "Brilliant, confirmed for Wednesday at 2pm. Speak then Lisa!" }] },
  { id: 7, name: "Michael Barrett", phone: "+44 7700 777888", status: "TRANSFERRED", attempts: 1, lastCalled: "Yesterday", duration: "6m 22s", transcript: [{ role: "agent", text: "Hi Michael, calling from Goldstone Capital." }, { role: "lead", text: "I know Goldstone actually — my colleague mentioned you." }, { role: "agent", text: "That's great to hear! Given your existing interest, would you like me to connect you directly with one of our senior advisors right now?" }, { role: "lead", text: "Yes, that would actually be useful." }, { role: "agent", text: "Excellent, I'm transferring you now. One moment." }] },
  { id: 8, name: "Claire Newton", phone: "+44 7911 999000", status: "PENDING", attempts: 0, lastCalled: "—", duration: "—", transcript: [] },
];

const NUMBERS = [
  { id: 1, number: "+44 20 7946 0101", status: "active", callsToday: 87, totalCalls: 412, lastUsed: "2 min ago", health: "good" },
  { id: 2, number: "+44 20 7946 0102", status: "active", callsToday: 91, totalCalls: 438, lastUsed: "4 min ago", health: "good" },
  { id: 3, number: "+44 20 7946 0103", status: "active", callsToday: 76, totalCalls: 389, lastUsed: "6 min ago", health: "good" },
  { id: 4, number: "+44 20 7946 0104", status: "active", callsToday: 83, totalCalls: 401, lastUsed: "1 min ago", health: "good" },
  { id: 5, number: "+44 20 7946 0105", status: "cooling", callsToday: 100, totalCalls: 512, lastUsed: "18 min ago", health: "warning" },
  { id: 6, number: "+44 20 7946 0106", status: "active", callsToday: 69, totalCalls: 344, lastUsed: "9 min ago", health: "good" },
  { id: 7, number: "+44 20 7946 0107", status: "active", callsToday: 72, totalCalls: 361, lastUsed: "3 min ago", health: "good" },
  { id: 8, number: "+44 20 7946 0108", status: "flagged", callsToday: 45, totalCalls: 298, lastUsed: "2h ago", health: "danger" },
  { id: 9, number: "+44 20 7946 0109", status: "active", callsToday: 88, totalCalls: 422, lastUsed: "5 min ago", health: "good" },
  { id: 10, number: "+44 20 7946 0110", status: "active", callsToday: 79, totalCalls: 397, lastUsed: "7 min ago", health: "good" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const outcomeConfig = {
  BOOKED: { bg: C.successSoft, color: C.success, label: "Booked" },
  CALLBACK: { bg: C.primarySoft, color: C.primaryDeep, label: "Callback" },
  NOT_INTERESTED: { bg: C.dangerSoft, color: C.danger, label: "Not interested" },
  NO_ANSWER: { bg: C.warningSoft, color: C.warning, label: "No answer" },
  DNC: { bg: "#f3f4f6", color: "#6b7280", label: "DNC" },
  TRANSFERRED: { bg: C.purpleSoft, color: C.purple, label: "Transferred" },
  PENDING: { bg: C.bgSoft, color: C.textMuted, label: "Pending" },
};

function Badge({ status, size = "sm" }) {
  const s = outcomeConfig[status] || outcomeConfig.PENDING;
  return <span style={{ background: s.bg, color: s.color, fontSize: size === "sm" ? 11 : 12, fontWeight: 500, padding: size === "sm" ? "3px 10px" : "4px 12px", borderRadius: 999 }}>{s.label}</span>;
}

function Btn({ children, onClick, variant = "primary", small, disabled, style: sx }) {
  const base = { fontFamily: "'DM Sans',sans-serif", fontWeight: 500, cursor: disabled ? "not-allowed" : "pointer", border: "none", borderRadius: 999, transition: "all 0.18s", opacity: disabled ? 0.5 : 1, ...sx };
  const variants = {
    primary: { background: `linear-gradient(135deg,${C.primaryStrong},${C.primaryDeep})`, color: "#fff", padding: small ? "7px 18px" : "10px 24px", fontSize: small ? 13 : 14, boxShadow: `0 2px 8px rgba(21,94,239,0.25)` },
    secondary: { background: C.surface, color: C.textSoft, padding: small ? "7px 18px" : "10px 24px", fontSize: small ? 13 : 14, border: `1px solid ${C.border}` },
    ghost: { background: "transparent", color: C.textMuted, padding: small ? "7px 14px" : "10px 20px", fontSize: small ? 13 : 14 },
    danger: { background: C.dangerSoft, color: C.danger, padding: small ? "7px 18px" : "10px 24px", fontSize: small ? 13 : 14 },
  };
  return <button onClick={onClick} disabled={disabled} style={{ ...base, ...variants[variant] }}>{children}</button>;
}

function Card({ children, style: sx, hover }) {
  const [hov, setHov] = useState(false);
  return <div onMouseEnter={() => hover && setHov(true)} onMouseLeave={() => setHov(false)} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 20, boxShadow: hov ? shadowHover : shadow, transition: "box-shadow 0.2s, transform 0.2s", transform: hov ? "translateY(-2px)" : "none", ...sx }}>{children}</div>;
}

const ICONS = {
  people: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  phone: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.38 2 2 0 0 1 3.58 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.6a16 16 0 0 0 6.29 6.29l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  check: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
  refresh: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>,
};

function StatCard({ label, value, sub, accent, icon }) {
  const iconEl = ICONS[icon];
  return (
    <Card style={{ padding: "22px 26px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.9, marginBottom: 10 }}>{label}</div>
          <div style={{ fontSize: 30, fontWeight: 600, color: accent || C.text, lineHeight: 1 }}>{value}</div>
          {sub && <div style={{ fontSize: 12, color: C.textMuted, marginTop: 6 }}>{sub}</div>}
        </div>
        {iconEl && (
          <div style={{ width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", background: C.bgSoft, borderRadius: 8, flexShrink: 0, color: accent || C.textMuted }}>
            {iconEl}
          </div>
        )}
      </div>
    </Card>
  );
}

function ProgressBar({ value, max, color, height = 6 }) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  return <div style={{ background: C.bgSoft, borderRadius: 999, height, overflow: "hidden" }}><div style={{ width: `${pct}%`, height: "100%", background: color || C.primaryStrong, borderRadius: 999, transition: "width 0.5s ease" }} /></div>;
}

function LiveDot() {
  return <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: C.successSoft, color: C.success, fontSize: 11, fontWeight: 500, padding: "3px 10px", borderRadius: 999 }}><span style={{ width: 6, height: 6, borderRadius: "50%", background: C.success, animation: "pulse 1.5s infinite" }} />Live</span>;
}

const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&family=DM+Sans:wght@300;400;500;600&display=swap');
*{box-sizing:border-box;margin:0;padding:0;}
body{font-family:'DM Sans',sans-serif;background:${C.bg};color:${C.text};}
@keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.3}}
@keyframes spin{to{transform:rotate(360deg)}}
::-webkit-scrollbar{width:4px;height:4px}
::-webkit-scrollbar-track{background:transparent}
::-webkit-scrollbar-thumb{background:${C.border};border-radius:4px}
input[type=file]{display:none}
`;

// ─── Login ────────────────────────────────────────────────────────────────────
function Login({ onLogin }) {
  const [u, setU] = useState(""); const [p, setP] = useState(""); const [err, setErr] = useState(""); const [loading, setLoading] = useState(false);
  const inp = { width: "100%", padding: "12px 16px", border: `1px solid ${C.border}`, borderRadius: 14, fontSize: 14, color: C.text, fontFamily: "'DM Sans',sans-serif", background: C.surface, outline: "none" };
  const submit = () => {
    setErr(""); setLoading(true);
    setTimeout(() => {
      const user = USERS[u];
      if (user && user.password === p) { onLogin({ username: u, ...user }); }
      else { setErr("Invalid username or password."); setLoading(false); }
    }, 600);
  };
  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", animation: "fadeUp 0.4s ease" }}>
      <div style={{ width: "100%", maxWidth: 420, padding: "0 24px" }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 38, fontWeight: 600, color: C.text, letterSpacing: -1 }}>briix<span style={{ color: C.primaryDeep }}>.</span></div>
          <div style={{ fontSize: 14, color: C.textMuted, marginTop: 8 }}>Sign in to your dashboard</div>
        </div>
        <Card style={{ padding: "36px 32px" }}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 500, color: C.textMuted, display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.8 }}>Username</label>
            <input style={inp} value={u} onChange={e => setU(e.target.value)} placeholder="Enter username" onKeyDown={e => e.key === "Enter" && submit()} />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={{ fontSize: 12, fontWeight: 500, color: C.textMuted, display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.8 }}>Password</label>
            <input style={inp} type="password" value={p} onChange={e => setP(e.target.value)} placeholder="Enter password" onKeyDown={e => e.key === "Enter" && submit()} />
          </div>
          {err && <div style={{ background: C.dangerSoft, color: C.danger, fontSize: 13, padding: "10px 14px", borderRadius: 12, marginBottom: 16 }}>{err}</div>}
          <Btn onClick={submit} disabled={loading} style={{ width: "100%", borderRadius: 14 }}>{loading ? "Signing in…" : "Sign in"}</Btn>
        </Card>
        <div style={{ textAlign: "center", marginTop: 24, fontSize: 12, color: C.textFaint }}>Demo: admin / admin123 or client / client123</div>
      </div>
    </div>
  );
}

// ─── Nav ──────────────────────────────────────────────────────────────────────
function Nav({ user, page, setPage, onLogout }) {
  const adminLinks = [{ id: "overview", label: "Overview" }, { id: "leads", label: "Leads" }, { id: "numbers", label: "Numbers" }, { id: "upload", label: "Upload CSV" }];
  const clientLinks = [{ id: "overview", label: "Overview" }, { id: "leads", label: "Bookings" }];
  const links = user.role === "admin" ? adminLinks : clientLinks;
  return (
    <nav style={{ background: "rgba(255,255,255,0.94)", borderBottom: `1px solid ${C.border}`, backdropFilter: "blur(12px)", position: "sticky", top: 0, zIndex: 100, padding: "0 40px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", height: 58, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 36 }}>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, fontWeight: 600, color: C.text, letterSpacing: -0.5, cursor: "pointer" }} onClick={() => setPage("overview")}>briix<span style={{ color: C.primaryDeep }}>.</span></div>
          <div style={{ display: "flex", gap: 4 }}>
            {links.map(l => (
              <button key={l.id} onClick={() => setPage(l.id)} style={{ padding: "6px 14px", borderRadius: 999, border: "none", background: page === l.id ? C.primarySoft : "transparent", color: page === l.id ? C.primaryDeep : C.textMuted, fontSize: 13, fontWeight: page === l.id ? 500 : 400, cursor: "pointer", fontFamily: "'DM Sans',sans-serif", transition: "all 0.15s" }}>{l.label}</button>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontSize: 12, color: C.textFaint }}>{user.name}</div>
          <div style={{ width: 32, height: 32, borderRadius: "50%", background: C.primarySoft, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, color: C.primaryDeep }}>{user.name[0]}</div>
          <Btn variant="ghost" small onClick={onLogout} style={{ fontSize: 12, color: C.textMuted }}>Sign out</Btn>
        </div>
      </div>
    </nav>
  );
}

// ─── Overview — Admin ─────────────────────────────────────────────────────────
function AdminOverview({ onLeadClick }) {
  const called = 8450, total = 22000, booked = 338, answered = 2112;
  return (
    <div style={{ padding: "32px 40px", maxWidth: 1280, margin: "0 auto", animation: "fadeUp 0.3s ease" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
        <div>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 26, fontWeight: 600, color: C.text, letterSpacing: -0.5 }}>Gold Investment Outreach — Q2 2026</div>
          <div style={{ display: "flex", gap: 10, marginTop: 8 }}><LiveDot /><span style={{ fontSize: 13, color: C.textMuted }}>Campaign running · Started Apr 1, 2026</span></div>
        </div>
        <Card style={{ padding: "14px 20px", textAlign: "right" }}>
          <div style={{ fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 4 }}>Today's dials</div>
          <div style={{ fontSize: 26, fontWeight: 600, color: C.primaryDeep }}>423</div>
        </Card>
      </div>

      <Card style={{ padding: "18px 24px", marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: C.textSoft }}>Campaign progress</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.primaryDeep }}>{Math.round((called / total) * 100)}% — {called.toLocaleString()} of {total.toLocaleString()} leads dialled</span>
        </div>
        <ProgressBar value={called} max={total} />
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 16, marginBottom: 24 }}>
        <StatCard label="Total leads" value={total.toLocaleString()} sub="In campaign" icon="people" />
        <StatCard label="Leads called" value={called.toLocaleString()} sub="25% answer rate" icon="phone" />
        <StatCard label="Bookings" value={booked.toLocaleString()} sub={`${((booked/answered)*100).toFixed(1)}% of answered`} accent={C.success} icon="check" />
        <StatCard label="Callbacks" value="445" sub="Scheduled follow-ups" accent={C.primaryDeep} icon="refresh" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 24 }}>
        <Card style={{ padding: "24px 28px" }}>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 18, fontWeight: 600, marginBottom: 20 }}>Lead pipeline breakdown</div>
          {[{ s: "PENDING", n: 13550 }, { s: "NO_ANSWER", n: 6338 }, { s: "CALLBACK", n: 445 }, { s: "BOOKED", n: 338 }, { s: "NOT_INTERESTED", n: 891 }, { s: "TRANSFERRED", n: 339 }, { s: "DNC", n: 87 }].map(({ s, n }) => {
            const conf = outcomeConfig[s];
            return (
              <div key={s} style={{ marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                  <span style={{ fontSize: 12, color: C.textMuted, fontWeight: 500 }}>{conf.label}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: conf.color }}>{n.toLocaleString()}</span>
                </div>
                <ProgressBar value={n} max={total} color={conf.color} />
              </div>
            );
          })}
        </Card>

        <Card style={{ padding: "24px 28px" }}>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 18, fontWeight: 600, marginBottom: 20 }}>Recent calls</div>
          {LEADS.filter(l => l.lastCalled !== "—").slice(0, 6).map((l, i, arr) => (
            <div key={l.id} onClick={() => onLeadClick(l)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: i < arr.length - 1 ? `1px solid ${C.border}` : "none", cursor: "pointer" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: C.text }}>{l.name}</div>
                <div style={{ fontSize: 11, color: C.textFaint, marginTop: 2 }}>{l.phone} · {l.lastCalled}</div>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <span style={{ fontSize: 11, color: C.textMuted }}>{l.duration}</span>
                <Badge status={l.status} />
              </div>
            </div>
          ))}
        </Card>
      </div>

      <Card style={{ padding: "24px 28px" }}>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 18, fontWeight: 600, marginBottom: 20 }}>Monthly infrastructure cost</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 16 }}>
          {[{ label: "Vapi voice AI", val: "£625" }, { label: "Telnyx telephony", val: "£225" }, { label: "LLM + Orchestration", val: "£250" }, { label: "Hosting + Numbers", val: "£120", accent: true }].map(x => (
            <div key={x.label} style={{ background: x.accent ? C.primarySoft : C.bgSoft, border: `1px solid ${x.accent ? C.borderStrong : C.border}`, borderRadius: 14, padding: "16px 20px" }}>
              <div style={{ fontSize: 11, color: x.accent ? C.primaryDeep : C.textMuted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>{x.label}</div>
              <div style={{ fontSize: 22, fontWeight: 600, color: x.accent ? C.primaryDeep : C.text }}>{x.val}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ─── Overview — Client ────────────────────────────────────────────────────────
function ClientOverview({ onLeadClick }) {
  const bookings = LEADS.filter(l => l.status === "BOOKED");
  return (
    <div style={{ padding: "32px 40px", maxWidth: 1280, margin: "0 auto", animation: "fadeUp 0.3s ease" }}>
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 26, fontWeight: 600, color: C.text, letterSpacing: -0.5 }}>Your Campaign Overview</div>
        <div style={{ display: "flex", gap: 10, marginTop: 8 }}><LiveDot /><span style={{ fontSize: 13, color: C.textMuted }}>Gold Investment Outreach · Running now</span></div>
      </div>

      <div style={{ background: `linear-gradient(135deg,${C.primarySoft},#f0f7ff)`, border: `1px solid ${C.borderStrong}`, borderRadius: 20, padding: "24px 28px", marginBottom: 24 }}>
        <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 8 }}>What's happening right now</div>
        <div style={{ fontSize: 15, color: C.textSoft, lineHeight: 1.7 }}>Your AI voice agent is actively calling through your lead list. Every lead is contacted up to 3 times. When someone agrees to a consultation, it's automatically logged below. Your advisors receive booking confirmations instantly.</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 16, marginBottom: 24 }}>
        <StatCard label="Total leads loaded" value="22,000" sub="In your campaign" />
        <StatCard label="Leads contacted" value="8,450" sub="38% of your list reached" />
        <StatCard label="Bookings secured" value="338" sub="16% booking rate" accent={C.success} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 24 }}>
        <Card style={{ padding: "24px 28px" }}>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 18, fontWeight: 600, marginBottom: 6 }}>What each outcome means</div>
          <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 20 }}>Plain-English guide to your results</div>
          {[
            { s: "BOOKED", desc: "Lead agreed to a consultation. Your advisor will receive a calendar invite." },
            { s: "CALLBACK", desc: "Lead asked to be called back at a specific time. The agent will retry." },
            { s: "NOT_INTERESTED", desc: "Lead declined. They won't be contacted again." },
            { s: "NO_ANSWER", desc: "No one picked up. The agent will try again up to 3 times total." },
            { s: "TRANSFERRED", desc: "High-intent lead connected directly to a senior advisor." },
          ].map(x => (
            <div key={x.s} style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
              <Badge status={x.s} />
              <span style={{ fontSize: 12, color: C.textMuted, lineHeight: 1.6, paddingTop: 2 }}>{x.desc}</span>
            </div>
          ))}
        </Card>

        <Card style={{ padding: "24px 28px" }}>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 18, fontWeight: 600, marginBottom: 20 }}>Latest bookings</div>
          {bookings.map((b, i) => (
            <div key={b.id} onClick={() => onLeadClick(b)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 0", borderBottom: i < bookings.length - 1 ? `1px solid ${C.border}` : "none", cursor: "pointer" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: C.primarySoft, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600, color: C.primaryDeep }}>{b.name.split(" ").map(n => n[0]).join("")}</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: C.text }}>{b.name}</div>
                  <div style={{ fontSize: 11, color: C.textFaint }}>{b.lastCalled}</div>
                </div>
              </div>
              <Badge status="BOOKED" />
            </div>
          ))}
        </Card>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Card style={{ padding: "22px 26px", background: C.successSoft, border: `1px solid #a7f3d0` }}>
          <div style={{ fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Booking rate</div>
          <div style={{ fontSize: 38, fontWeight: 600, color: C.success }}>16.0%</div>
          <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>of answered calls result in a booking</div>
        </Card>
        <Card style={{ padding: "22px 26px", background: C.primarySoft, border: `1px solid ${C.borderStrong}` }}>
          <div style={{ fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>Answer rate</div>
          <div style={{ fontSize: 38, fontWeight: 600, color: C.primaryDeep }}>25.0%</div>
          <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>of all dials are answered</div>
        </Card>
      </div>
    </div>
  );
}

// ─── Leads page ───────────────────────────────────────────────────────────────
function LeadsPage({ onLeadClick, role }) {
  const [filter, setFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const statuses = ["ALL", "BOOKED", "CALLBACK", "NO_ANSWER", "NOT_INTERESTED", "TRANSFERRED", "PENDING", "DNC"];
  const showStatuses = role === "client" ? ["ALL", "BOOKED", "CALLBACK", "NOT_INTERESTED"] : statuses;
  const filtered = LEADS.filter(l => {
    if (filter !== "ALL" && l.status !== filter) return false;
    if (search && !l.name.toLowerCase().includes(search.toLowerCase()) && !l.phone.includes(search)) return false;
    return true;
  });
  return (
    <div style={{ padding: "32px 40px", maxWidth: 1280, margin: "0 auto", animation: "fadeUp 0.3s ease" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 26, fontWeight: 600, color: C.text }}>{role === "client" ? "Bookings & Callbacks" : "All Leads"}</div>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or number…" style={{ padding: "9px 16px", border: `1px solid ${C.border}`, borderRadius: 999, fontSize: 13, color: C.text, fontFamily: "'DM Sans',sans-serif", background: C.surface, outline: "none", width: 240 }} />
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {showStatuses.map(s => {
          const conf = s === "ALL" ? { label: "All", color: C.textMuted, bg: C.bgSoft } : outcomeConfig[s];
          const active = filter === s;
          return <button key={s} onClick={() => setFilter(s)} style={{ padding: "6px 14px", borderRadius: 999, border: `1px solid ${active ? conf.color : C.border}`, background: active ? conf.bg : "transparent", color: active ? conf.color : C.textMuted, fontSize: 12, fontWeight: active ? 500 : 400, cursor: "pointer", fontFamily: "'DM Sans',sans-serif", transition: "all 0.15s" }}>{conf.label || s}</button>;
        })}
      </div>

      <Card style={{ overflow: "hidden" }}>
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        <table style={{ width: "100%", minWidth: 700, borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: C.bgSoft }}>
              {["Name", "Phone", "Status", "Attempts", "Last called", "Duration", ""].map(h => (
                <th key={h} style={{ padding: "12px 16px", fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: "uppercase", letterSpacing: 0.8, textAlign: "left", borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((l, i) => (
              <tr key={l.id} onClick={() => l.transcript.length > 0 && onLeadClick(l)} style={{ borderBottom: i < filtered.length - 1 ? `1px solid ${C.border}` : "none", cursor: l.transcript.length > 0 ? "pointer" : "default", transition: "background 0.15s" }} onMouseEnter={e => e.currentTarget.style.background = C.bgSoft} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <td style={{ padding: "12px 16px", whiteSpace: "nowrap" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 30, height: 30, borderRadius: "50%", background: C.primarySoft, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600, color: C.primaryDeep, flexShrink: 0 }}>{l.name.split(" ").map(n => n[0]).join("")}</div>
                    <span style={{ fontSize: 13, fontWeight: 500, color: C.text }}>{l.name}</span>
                  </div>
                </td>
                <td style={{ padding: "12px 16px", fontSize: 13, color: C.textMuted, whiteSpace: "nowrap" }}>{l.phone}</td>
                <td style={{ padding: "12px 16px", whiteSpace: "nowrap" }}><Badge status={l.status} /></td>
                <td style={{ padding: "12px 16px", fontSize: 13, color: C.textMuted, whiteSpace: "nowrap" }}>{l.attempts}</td>
                <td style={{ padding: "12px 16px", fontSize: 13, color: C.textMuted, whiteSpace: "nowrap" }}>{l.lastCalled}</td>
                <td style={{ padding: "12px 16px", fontSize: 13, color: C.textMuted, whiteSpace: "nowrap" }}>{l.duration}</td>
                <td style={{ padding: "12px 16px", whiteSpace: "nowrap" }}>{l.transcript.length > 0 && <span style={{ fontSize: 11, color: C.primaryDeep, fontWeight: 500 }}>View transcript →</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {filtered.length === 0 && <div style={{ padding: "40px", textAlign: "center", color: C.textFaint, fontSize: 14 }}>No leads match your filter.</div>}
      </Card>
    </div>
  );
}

// ─── Numbers page ─────────────────────────────────────────────────────────────
function NumbersPage() {
  const healthConfig = { good: { color: C.success, bg: C.successSoft, label: "Healthy" }, warning: { color: C.warning, bg: C.warningSoft, label: "Cooling down" }, danger: { color: C.danger, bg: C.dangerSoft, label: "Flagged" } };
  const statusConfig = { active: { color: C.success, label: "Active" }, cooling: { color: C.warning, label: "Cooling" }, flagged: { color: C.danger, label: "Flagged" } };
  const totalToday = NUMBERS.reduce((a, n) => a + n.callsToday, 0);
  const active = NUMBERS.filter(n => n.status === "active").length;
  return (
    <div style={{ padding: "32px 40px", maxWidth: 1280, margin: "0 auto", animation: "fadeUp 0.3s ease" }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 26, fontWeight: 600, color: C.text }}>Active Numbers</div>
        <div style={{ fontSize: 13, color: C.textMuted, marginTop: 6 }}>Telnyx numbers in rotation — spread across calls to protect deliverability</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 16, marginBottom: 28 }}>
        <StatCard label="Total numbers" value={NUMBERS.length} />
        <StatCard label="Active now" value={active} accent={C.success} />
        <StatCard label="Calls today" value={totalToday.toLocaleString()} sub="across all numbers" />
        <StatCard label="Flagged" value={NUMBERS.filter(n => n.health === "danger").length} accent={C.danger} />
      </div>

      <Card style={{ overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: C.bgSoft }}>
              {["Number", "Status", "Health", "Calls today", "Total calls", "Last used", ""].map(h => (
                <th key={h} style={{ padding: "12px 16px", fontSize: 11, fontWeight: 500, color: C.textMuted, textTransform: "uppercase", letterSpacing: 0.8, textAlign: "left", borderBottom: `1px solid ${C.border}` }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {NUMBERS.map((n, i) => {
              const h = healthConfig[n.health];
              const st = statusConfig[n.status];
              return (
                <tr key={n.id} style={{ borderBottom: i < NUMBERS.length - 1 ? `1px solid ${C.border}` : "none" }}>
                  <td style={{ padding: "13px 16px", fontSize: 13, fontWeight: 500, color: C.text, fontFamily: "monospace" }}>{n.number}</td>
                  <td style={{ padding: "13px 16px" }}><span style={{ fontSize: 11, fontWeight: 500, color: st.color }}>{st.label}</span></td>
                  <td style={{ padding: "13px 16px" }}><span style={{ background: h.bg, color: h.color, fontSize: 11, fontWeight: 500, padding: "3px 10px", borderRadius: 999 }}>{h.label}</span></td>
                  <td style={{ padding: "13px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 13, color: C.text, minWidth: 28 }}>{n.callsToday}</span>
                      <ProgressBar value={n.callsToday} max={100} color={n.health === "danger" ? C.danger : n.health === "warning" ? C.warning : C.primaryStrong} height={4} />
                    </div>
                  </td>
                  <td style={{ padding: "13px 16px", fontSize: 13, color: C.textMuted }}>{n.totalCalls.toLocaleString()}</td>
                  <td style={{ padding: "13px 16px", fontSize: 13, color: C.textMuted }}>{n.lastUsed}</td>
                  <td style={{ padding: "13px 16px" }}>
                    {n.health === "danger" && <span style={{ fontSize: 11, color: C.danger, fontWeight: 500 }}>⚠ Needs review</span>}
                    {n.health === "warning" && <span style={{ fontSize: 11, color: C.warning, fontWeight: 500 }}>Cooling off</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ─── CSV Upload ───────────────────────────────────────────────────────────────
function UploadPage() {
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("idle");
  const [progress, setProgress] = useState(0);
  const fileRef = useRef();

  const parseCSV = (text) => {
    const lines = text.trim().split("\n");
    const headers = lines[0].split(",").map(h => h.trim().toLowerCase());
    return lines.slice(1).map((line, i) => {
      const vals = line.split(",").map(v => v.trim().replace(/^"|"$/g, ""));
      const row = {};
      headers.forEach((h, j) => row[h] = vals[j] || "");
      row._line = i + 2;
      row._valid = !!(row.firstname && row.phone);
      row._error = !row.firstname ? "Missing first name" : !row.phone ? "Missing phone" : null;
      return row;
    });
  };

  const handleFile = (f) => {
    if (!f || !f.name.endsWith(".csv")) return;
    setFile(f); setStatus("preview");
    const reader = new FileReader();
    reader.onload = e => {
      const parsed = parseCSV(e.target.result);
      setRows(parsed);
    };
    reader.readAsText(f);
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const doImport = () => {
    setStatus("importing"); setProgress(0);
    const valid = rows.filter(r => r._valid);
    let i = 0;
    const tick = setInterval(() => {
      i += Math.floor(Math.random() * 80) + 20;
      setProgress(Math.min(100, Math.round((i / valid.length) * 100)));
      if (i >= valid.length) { clearInterval(tick); setStatus("done"); }
    }, 120);
  };

  const reset = () => { setFile(null); setRows([]); setStatus("idle"); setProgress(0); };

  const valid = rows.filter(r => r._valid);
  const invalid = rows.filter(r => !r._valid);

  return (
    <div style={{ padding: "32px 40px", maxWidth: 1280, margin: "0 auto", animation: "fadeUp 0.3s ease" }}>
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 26, fontWeight: 600, color: C.text }}>Upload Leads</div>
        <div style={{ fontSize: 13, color: C.textMuted, marginTop: 6 }}>Drop a CSV file and the agent will begin dialling automatically</div>
      </div>

      {status === "idle" && (
        <>
          <div onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop} onClick={() => fileRef.current.click()} style={{ border: `2px dashed ${dragOver ? C.primaryStrong : C.border}`, borderRadius: 20, padding: "60px 40px", textAlign: "center", cursor: "pointer", background: dragOver ? C.primarySoft : C.surface, transition: "all 0.2s", marginBottom: 24 }}>
            <div style={{ fontSize: 32, marginBottom: 16 }}>📂</div>
            <div style={{ fontSize: 15, fontWeight: 500, color: C.text, marginBottom: 8 }}>Drop your CSV here, or click to browse</div>
            <div style={{ fontSize: 13, color: C.textMuted }}>Accepts .csv files only</div>
            <input ref={fileRef} type="file" accept=".csv" onChange={e => handleFile(e.target.files[0])} />
          </div>
          <Card style={{ padding: "20px 24px" }}>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Required CSV format</div>
            <div style={{ fontFamily: "monospace", fontSize: 12, background: C.bgSoft, padding: "12px 16px", borderRadius: 12, color: C.textSoft, lineHeight: 1.8 }}>
              firstName, lastName, phone, email, timezone, consentDate<br />
              James, Thornton, +447911123456, j.thornton@email.com, Europe/London, 2026-01-15<br />
              Sarah, Mitchell, +447700987654, s.mitchell@email.com, Europe/London, 2026-01-20
            </div>
            <div style={{ fontSize: 12, color: C.textMuted, marginTop: 12 }}>Required: <strong>firstName</strong>, <strong>phone</strong> · Optional: lastName, email, timezone, consentDate · Leads without consentDate will be flagged</div>
          </Card>
        </>
      )}

      {status === "preview" && (
        <>
          <Card style={{ padding: "20px 24px", marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 500, color: C.text }}>{file.name}</div>
                <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>{rows.length.toLocaleString()} rows found · <span style={{ color: C.success }}>{valid.length.toLocaleString()} valid</span> · <span style={{ color: C.danger }}>{invalid.length} errors</span></div>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <Btn variant="secondary" small onClick={reset}>Cancel</Btn>
                <Btn small onClick={doImport} disabled={valid.length === 0}>Import {valid.length.toLocaleString()} leads</Btn>
              </div>
            </div>
          </Card>

          {invalid.length > 0 && (
            <Card style={{ padding: "16px 20px", background: C.dangerSoft, border: `1px solid #fca5a5`, marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: C.danger, marginBottom: 8 }}>⚠ {invalid.length} rows will be skipped</div>
              {invalid.slice(0, 3).map(r => <div key={r._line} style={{ fontSize: 12, color: C.danger, marginBottom: 2 }}>Row {r._line}: {r._error}</div>)}
              {invalid.length > 3 && <div style={{ fontSize: 12, color: C.danger }}>…and {invalid.length - 3} more</div>}
            </Card>
          )}

          <Card style={{ overflow: "hidden" }}>
            <div style={{ padding: "12px 16px", background: C.bgSoft, borderBottom: `1px solid ${C.border}`, fontSize: 12, fontWeight: 500, color: C.textMuted, textTransform: "uppercase", letterSpacing: 0.8 }}>Preview (first 5 rows)</div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>{["First name", "Last name", "Phone", "Email", "Consent date", ""].map(h => <th key={h} style={{ padding: "10px 16px", fontSize: 11, fontWeight: 500, color: C.textMuted, textAlign: "left", borderBottom: `1px solid ${C.border}` }}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {rows.slice(0, 5).map((r, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${C.border}`, background: !r._valid ? C.dangerSoft : "transparent" }}>
                    <td style={{ padding: "10px 16px", fontSize: 13, color: C.text }}>{r.firstname || r.firstName || "—"}</td>
                    <td style={{ padding: "10px 16px", fontSize: 13, color: C.textMuted }}>{r.lastname || r.lastName || "—"}</td>
                    <td style={{ padding: "10px 16px", fontSize: 13, fontFamily: "monospace", color: C.text }}>{r.phone || "—"}</td>
                    <td style={{ padding: "10px 16px", fontSize: 13, color: C.textMuted }}>{r.email || "—"}</td>
                    <td style={{ padding: "10px 16px", fontSize: 13, color: C.textMuted }}>{r.consentdate || r.consentDate || <span style={{ color: C.warning }}>Missing</span>}</td>
                    <td style={{ padding: "10px 16px" }}>{r._valid ? <span style={{ color: C.success, fontSize: 11 }}>✓</span> : <span style={{ color: C.danger, fontSize: 11 }} title={r._error}>✕</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}

      {status === "importing" && (
        <Card style={{ padding: "60px 40px", textAlign: "center" }}>
          <div style={{ fontSize: 48, fontWeight: 600, color: C.primaryDeep, marginBottom: 12 }}>{progress}%</div>
          <div style={{ fontSize: 15, fontWeight: 500, color: C.text, marginBottom: 20 }}>Importing {valid.length.toLocaleString()} leads…</div>
          <div style={{ maxWidth: 400, margin: "0 auto" }}><ProgressBar value={progress} max={100} /></div>
          <div style={{ fontSize: 13, color: C.textMuted, marginTop: 16 }}>Running consent checks · Removing duplicates · Queuing for dialler</div>
        </Card>
      )}

      {status === "done" && (
        <Card style={{ padding: "60px 40px", textAlign: "center" }}>
          <div style={{ fontSize: 44, marginBottom: 16 }}>✅</div>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 24, fontWeight: 600, color: C.text, marginBottom: 8 }}>{valid.length.toLocaleString()} leads imported successfully</div>
          <div style={{ fontSize: 14, color: C.textMuted, marginBottom: 28 }}>The voice agent will begin dialling automatically within 5 minutes during calling hours (9am–8pm)</div>
          <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
            <Btn onClick={reset} variant="secondary">Upload another file</Btn>
            <Btn>View leads</Btn>
          </div>
        </Card>
      )}
    </div>
  );
}

// ─── Transcript modal ─────────────────────────────────────────────────────────
function TranscriptModal({ lead, onClose }) {
  if (!lead) return null;
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.4)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, backdropFilter: "blur(4px)", animation: "fadeUp 0.2s ease" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.surface, borderRadius: 24, width: "100%", maxWidth: 580, maxHeight: "80vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(21,94,239,0.15)" }}>
        <div style={{ padding: "24px 28px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 20, fontWeight: 600, color: C.text }}>{lead.name}</div>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 6 }}>
              <span style={{ fontSize: 12, color: C.textMuted }}>{lead.phone}</span>
              <span style={{ fontSize: 12, color: C.textMuted }}>·</span>
              <Badge status={lead.status} />
              <span style={{ fontSize: 12, color: C.textMuted }}>·</span>
              <span style={{ fontSize: 12, color: C.textMuted }}>{lead.duration}</span>
            </div>
          </div>
          <button onClick={onClose} style={{ background: C.bgSoft, border: "none", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", fontSize: 16, color: C.textMuted, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
        </div>
        <div style={{ padding: "24px 28px", overflowY: "auto", flex: 1 }}>
          {lead.transcript.length === 0 ? (
            <div style={{ textAlign: "center", color: C.textFaint, padding: "40px 0", fontSize: 14 }}>No transcript available — call was not answered.</div>
          ) : (
            lead.transcript.map((t, i) => (
              <div key={i} style={{ display: "flex", justifyContent: t.role === "agent" ? "flex-start" : "flex-end", marginBottom: 14 }}>
                <div style={{ maxWidth: "78%", background: t.role === "agent" ? C.bgSoft : C.primarySoft, border: `1px solid ${t.role === "agent" ? C.border : C.borderStrong}`, borderRadius: t.role === "agent" ? "4px 16px 16px 16px" : "16px 4px 16px 16px", padding: "10px 14px" }}>
                  <div style={{ fontSize: 10, fontWeight: 500, color: t.role === "agent" ? C.primaryDeep : C.textMuted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 4 }}>{t.role === "agent" ? "AI Agent" : "Lead"}</div>
                  <div style={{ fontSize: 13, color: C.textSoft, lineHeight: 1.6 }}>{t.text}</div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(null);
  const [page, setPage] = useState("overview");
  const [selectedLead, setSelectedLead] = useState(null);

  const handleLogin = (u) => { setUser(u); setPage("overview"); };
  const handleLogout = () => { setUser(null); setPage("overview"); };
  const handleLeadClick = (lead) => setSelectedLead(lead);

  return (
    <>
      <style>{GLOBAL_CSS}</style>
      {!user ? (
        <Login onLogin={handleLogin} />
      ) : (
        <div style={{ minHeight: "100vh", background: C.bg }}>
          <Nav user={user} page={page} setPage={setPage} onLogout={handleLogout} />
          {page === "overview" && user.role === "admin" && <AdminOverview onLeadClick={handleLeadClick} />}
          {page === "overview" && user.role === "client" && <ClientOverview onLeadClick={handleLeadClick} />}
          {page === "leads" && <LeadsPage onLeadClick={handleLeadClick} role={user.role} />}
          {page === "numbers" && user.role === "admin" && <NumbersPage />}
          {page === "upload" && user.role === "admin" && <UploadPage />}
          <TranscriptModal lead={selectedLead} onClose={() => setSelectedLead(null)} />
        </div>
      )}
    </>
  );
}
